import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getChannelsEndpoint } from '@gitbook/env';
import { Logger } from '@gitbook/util-debug';
import { useLiveRef } from '@gitbook/util-react-hooks';

/**
 * Timeout for closing a channel that has no listeners.
 */
const CHANNEL_GC_TIMEOUT = 10000;

const channels = new Map<
    string,
    {
        eventSource: EventSource;
        data: undefined | api.SubscriptionChannelData['data'];
        listeners: Array<(data: api.SubscriptionChannelData['data']) => void>;
        gcTimeout?: NodeJS.Timeout;
    }
>();

const logger = Logger('channels');

/**
 * React hook to listen to updates on a realtime channel.
 */
export function useRealtimeChannel<Channel extends api.SubscriptionChannel>(
    channel: Channel,
    /** Optional callback for each update received */
    callback?: (data: api.DataForSubscriptionChannel<Channel>) => void,
    options?: { skip?: boolean }
): api.DataForSubscriptionChannel<Channel> | null {
    const channelId = api.getSubscriptionChannelId(channel);
    const skip = options?.skip ?? false;

    const [data, setData] = React.useState<api.DataForSubscriptionChannel<Channel> | null>(() => {
        const channel = channels.get(channelId);
        return (channel?.data as api.DataForSubscriptionChannel<Channel>) ?? null;
    });
    const callbackRef = useLiveRef(callback);

    React.useEffect(() => {
        if (skip) {
            setData(null);
            return;
        }
        return subscribeToRealtimeChannelId<api.DataForSubscriptionChannel<Channel>>(
            channelId,
            (newData) => {
                setData(newData);
                if (callbackRef.current) {
                    callbackRef.current(newData);
                }
            }
        );
    }, [channelId, callbackRef, skip]);

    return data;
}

/**
 * Get the initial data for a channel if any.
 */
export function getInitialDataForChannel<Channel extends api.SubscriptionChannel>(
    channel: Channel
): api.DataForSubscriptionChannel<Channel> | null {
    const channelId = api.getSubscriptionChannelId(channel);
    const existingChannel = channels.get(channelId);
    return (existingChannel?.data as api.DataForSubscriptionChannel<Channel>) ?? null;
}

/**
 * Subscribe to updates on a realtime channel.
 */
export function subscribeToRealtimeChannel<Channel extends api.SubscriptionChannel>(
    channel: Channel,
    callback: (data: api.DataForSubscriptionChannel<Channel>) => void
): () => void {
    const channelId = api.getSubscriptionChannelId(channel);
    const data = getInitialDataForChannel(channel);
    if (data) {
        callback(data);
    }
    return subscribeToRealtimeChannelId(channelId, callback);
}

/**
 * Subscribe to a realtime channel by ID.
 */
export function subscribeToRealtimeChannelId<Data extends api.SubscriptionChannelData['data']>(
    channelId: string,
    callback: (data: Data) => void
): () => void {
    let channel = channels.get(channelId);
    if (!channel) {
        logger.info(`opening channel "${channelId}"`);
        channel = {
            eventSource: new EventSource(getEventSourceURL(channelId)),
            data: undefined,
            listeners: [],
        };
        channels.set(channelId, channel);

        channel.eventSource.addEventListener('message', (event) => {
            const parsed = parseEventSourceMessage<Data>(event.data);
            logger.info(`received message from channel "${channelId}"`, parsed);
            if (parsed !== null && channel) {
                channel.data = parsed;
                channel.listeners.forEach((fn) => {
                    fn(parsed);
                });
            }
        });
    }

    channel.listeners.push(callback);
    return () => {
        channel.listeners = channel.listeners.filter((fn) => fn !== callback);

        if (channel.listeners.length === 0) {
            if (channel.gcTimeout) {
                clearTimeout(channel.gcTimeout);
            }

            // We don't directly close the channel here, as it might need to be used directly after because of react effect transition
            // It also helps ensure durign development where react calls hooks multiple times, we don't churn the websocket too often.
            channel.gcTimeout = setTimeout(() => {
                garbageCollectChannel(channelId);
            }, CHANNEL_GC_TIMEOUT);
        }
    };
}

function garbageCollectChannel(channelId: string) {
    const channel = channels.get(channelId);
    if (!channel) {
        return;
    }

    channel.gcTimeout = undefined;

    if (channel.listeners.length > 0) {
        return;
    }

    logger.info(`close channel "${channelId}"`);
    channel.eventSource.close();
    channels.delete(channelId);
}

/**
 * Get the WebSocket URL for a channel ID.
 */
function getEventSourceURL(channelId: string): URL {
    return new URL(`${getChannelsEndpoint()}/channels/${encodeURIComponent(channelId)}/events`);
}

/**
 * Parse an event source message.
 */
function parseEventSourceMessage<Data>(data: unknown): Data | null {
    if (typeof data !== 'string') {
        return null;
    }

    try {
        const parsed = JSON.parse(data) as unknown;
        if (parsed && typeof parsed === 'object' && 'data' in parsed) {
            // @ts-expect-error
            return parsed.data;
        }

        throw new Error('invalid event source message, missing "data" field');
    } catch (_err) {
        logger.error('failed to parse event source message', data, _err);
        return null;
    }
}
